import { observer } from "mobx-react-lite";
import { useTran, useTheme } from "@/hooks";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Zap, Layers } from "lucide-react";
import { LanguageSwitcher } from "@/components/common";
import { ThemeSwitcher } from "@/components/layout";
import { PATHS } from "@/lib/router";
import { useNavigate } from "react-router-dom";

export const SustainabilityPage = observer(() => {
  const { t } = useTran();
  const themeStore = useTheme();
  const navigate = useNavigate();

  return (
    <div
      className="flex h-screen min-h-[100dvh] flex-col overflow-hidden bg-background"
      data-theme={themeStore.theme}
    >
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top_left,hsl(var(--primary)/0.12),transparent_30%),radial-gradient(circle_at_bottom_right,hsl(var(--secondary)/0.12),transparent_32%)]" />

      <header className="relative z-10 shrink-0 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md">
        <div className="relative mx-auto flex h-14 w-full items-center justify-between gap-3 px-3 sm:px-4 lg:px-8">
          <div className="flex min-w-0 items-center">
            <img
              src="/logos/logo.svg"
              alt={t("auth.sustainability.logoAlt")}
              className="h-10 w-auto sm:h-12"
            />
          </div>
          <div className="flex flex-none items-center gap-2">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto w-full max-w-5xl">
          <h1 className="mb-4 text-center text-3xl font-black leading-tight tracking-tight text-black dark:text-white sm:text-4xl lg:text-5xl xl:text-6xl">
            {t("auth.sustainability.gatewayTo")}{" "}
            <span className="text-secondary dark:text-primary">
              {t("auth.sustainability.platformName")}
            </span>
          </h1>

          <p className="mx-auto mb-6 max-w-3xl text-center text-sm text-gray-600 dark:text-gray-400 sm:text-base lg:text-lg">
            {t("auth.sustainability.description")}
          </p>

          <div className="mb-6 flex flex-col items-center justify-center gap-3 sm:mb-8 sm:flex-row">
            <Button
              type="button"
              size="lg"
              onClick={() => navigate(PATHS.LOGIN)}
              className="w-full rounded-full sm:w-auto"
            >
              {t("auth.sustainability.cta")}
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 lg:gap-5">
            {[
              {
                icon: Zap,
                title: "auth.sustainability.featureSimplified",
                desc: "auth.sustainability.featureSimplifiedDesc",
              },
              {
                icon: ShieldCheck,
                title: "auth.sustainability.featureCompliance",
                desc: "auth.sustainability.featureComplianceDesc",
              },
              {
                icon: Layers,
                title: "auth.sustainability.featureManagement",
                desc: "auth.sustainability.featureManagementDesc",
              },
            ].map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="rounded-2xl border border-border bg-card/90 p-4 transition-colors hover:border-primary"
                >
                  <div className="mb-2 flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <p className="text-base font-medium text-black dark:text-white">
                      {t(feature.title)}
                    </p>
                  </div>
                  <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    {t(feature.desc)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <footer className="relative z-10 shrink-0 border-t border-border bg-background/90 px-4 py-3 text-center text-xs text-gray-800 backdrop-blur dark:text-gray-500 sm:px-6 sm:py-4 sm:text-sm md:px-12">
        <p>
          {t("auth.sustainability.poweredBy")}{" "}
          <span className="font-semibold text-black dark:text-gray-300">
            Evercomm
          </span>{" "}
          | {t("auth.sustainability.poweredTagline")}
        </p>
      </footer>
    </div>
  );
});
